// Web Audio helpers for the /scale-country sub-site — native AudioContext
// only, no library, same lightweight posture as keyboardPlan.js's engine
// (lazy shared AudioContext, audioSupported guard, exp attack/decay envelopes
// on plain oscillators).

let audioCtx;

// Lazily create (and resume) a single shared AudioContext. Must be called
// from a user gesture the first time so browsers allow playback. Returns
// null if the browser has no Web Audio support.
const getCtx = () => {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!audioCtx) audioCtx = new AC();
  if (audioCtx.state === "suspended") audioCtx.resume();
  return audioCtx;
};

// Whether this browser can synthesize tones at all (used to hide audio UI).
export const audioSupported = () =>
  typeof window !== "undefined" && Boolean(window.AudioContext || window.webkitAudioContext);

// Convert a MIDI note number to frequency in Hz (A4 = 69 = 440 Hz).
export const midiToFreq = (midi) => 440 * 2 ** ((midi - 69) / 12);

// Play a single tone: a plain oscillator with a quick exponential attack
// (~12ms) and exponential decay envelope, like keyboardPlan's playNote.
export const playTone = (midi, { whenSec = 0, durMs = 450, gain = 0.14, type = "triangle" } = {}) => {
  const ctx = getCtx();
  if (!ctx) return;
  const t0 = ctx.currentTime + whenSec;
  const dur = durMs / 1000;
  const g = ctx.createGain();
  g.connect(ctx.destination);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  const osc = ctx.createOscillator();
  osc.type = type;
  osc.frequency.value = midiToFreq(midi);
  osc.connect(g);
  osc.start(t0);
  osc.stop(t0 + dur + 0.05);
};

// Play a scale ascending: every semitone offset in `semis`, then the root an
// octave up, each note ~110ms after the last.
export const playScaleRun = (semis, rootMidi = 60) => {
  if (!getCtx()) return;
  const offsets = [...semis, 12];
  offsets.forEach((offset, i) => {
    playTone(rootMidi + offset, { whenSec: (i * 110) / 1000 });
  });
};

// Play the "character note" demo for a place: a low root pad, then the from-
// note, then the to-note, so the ear can hear the move that defines the
// scale's character.
export const playCharDemo = (charNote, rootMidi = 60) => {
  if (!getCtx()) return;
  playTone(rootMidi - 12, { whenSec: 0, durMs: 1800, gain: 0.06, type: "sine" });
  playTone(rootMidi + charNote.from, { whenSec: 0.15, durMs: 650, gain: 0.14 });
  playTone(rootMidi + charNote.to, { whenSec: 0.85, durMs: 1000, gain: 0.14 });
};

// Schedule a phrase (array of STEPS columns, each a list of midi numbers) —
// every note in every column at whenSec = i * stepMs/1000. Returns the total
// duration in ms.
export const playPhraseSteps = (steps, { stepMs = 280, gain = 0.13 } = {}) => {
  if (getCtx()) {
    steps.forEach((col, i) => {
      col.forEach((midi) => {
        playTone(midi, { whenSec: (i * stepMs) / 1000, durMs: stepMs * 0.9, gain });
      });
    });
  }
  return steps.length * stepMs;
};

// Start a sustained drone: sawtooth an octave below the root plus a triangle
// at the root, both through a lowpass at 380Hz, sharing one gain node ramped
// up to 0.05. Returns { stop } — stop() ramps the gain down then stops both
// oscillators ~800ms later. If audio isn't supported, stop() is a no-op.
export const startDrone = (rootMidi = 48) => {
  const ctx = getCtx();
  if (!ctx) return { stop: () => {} };
  const t0 = ctx.currentTime;

  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 380;
  filter.connect(ctx.destination);

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.setTargetAtTime(0.05, t0, 0.3);
  gain.connect(filter);

  const low = ctx.createOscillator();
  low.type = "sawtooth";
  low.frequency.value = midiToFreq(rootMidi - 12);
  low.connect(gain);
  low.start(t0);

  const root = ctx.createOscillator();
  root.type = "triangle";
  root.frequency.value = midiToFreq(rootMidi);
  root.connect(gain);
  root.start(t0);

  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    const tr = ctx.currentTime;
    gain.gain.setTargetAtTime(0.0001, tr, 0.15);
    low.stop(tr + 0.8);
    root.stop(tr + 0.8);
  };
  return { stop };
};
